
import { dayjs, DT_FORMAT_JA, D_FORMAT_JA } from '../../../src_server/common/momentExtends';
import { Component, Vue, Prop, Watch, Emit } from 'vue-property-decorator';

/**
 * テーブルのカラム
 */
export class TableColumn {
  /** カラムのヘッダに表示する文字列（必須） */
  label!: string;
  /** rowsのJsonのキー値(type=noneなら必須) */
  name?: string;
  width?: string;
  type?: 'seq' | 'button' | 'renderfunc' | 'date' | 'datetime' | 'currency' | 'currency_original' | 'none'; // default none
  /**
   * 関数の戻り値HTMLを使ってTD内を作成する
   */
  representedAs?: (row: any) => string;
  /**
   *
   */
  formatFunc?: (value: any) => string;
  /**
   * type=buttonの時、ボタンに設定するcss class
   */
  btnClass?: any;
  /**
   * TDに設定するcss class (align-middle:縦方向中央寄せは必ず入る)
   */
  tdClass?: any;
  /**
   * セル内の横位置デフォルトはmiddle
   */
  align?: 'middle' | 'left' | 'right';
  /**
   * type=buttonのときのクリック時処理
   */
  onclick?: (row: any, event: Event) => void;

  /**
   * マウスオーバー
   */
  onmouseover?: (row: any, col: any, event: Event) => void;

  /**
   * ソートのために利用する項目
   */
  sortitem?: string;

  /**
   * テーブルヘッダに設定するcss class
   */
  headerClass?: string;
  /**
   * ソート昇順
   */
  up?: boolean;
  /**
   * ソート降順
   */
  down?: boolean;
}

@Component({
  filters: {
    dateFormat: (dt: Date) => {
      return dayjs(dt).format(D_FORMAT_JA);
    },
    dateTimeFormat: (dt: Date) => {
      return dayjs(dt).format(DT_FORMAT_JA);
    },
    currencyFormat: (item: number) => {
      return item.toLocaleString('ja-JP', { style: 'currency', currency: 'JPY' });
    },
    currencyFormatOrigin: (item: number) => {
      return item.toLocaleString();
    },
  },
})
export default class CommonTable extends Vue {
  /**
   * 総行数を返す
   */
  public total = 1;

  /**
   * ページネイションの数
   */
  public paginations: number[] = [0];

  public currentPage = 1;

  @Prop({ default: 10 })
  public inPageCount!: number;

  mounted() {
    this.showPaginations();
    // console.log('table mounted', this.columns, this.rows);
  }

  @Watch('inPageCount')
  @Watch('rows')
  onRowsChanged() {
    this.showPaginations();
  }

  showPaginations() {
    // console.log('show pagenation start ', this.rows.length, this.inPageCount);

    if (this.rows.length % this.inPageCount > 0) {
      this.total = Math.floor(this.rows.length / this.inPageCount) + 1;
    } else {
      this.total = Math.floor(this.rows.length / this.inPageCount);
    }
    this.paginations = Array.from({ length: this.total }, (v, k) => k);
  }

  get inPageRows() {
    const start = (this.currentPage - 1) * this.inPageCount;
    const end = this.currentPage * this.inPageCount;
    // console.log(start, end);
    return this.rows.slice(start, end);
  }

  @Prop({ required: true })
  public columns!: TableColumn[];

  @Prop({ required: true })
  public rows!: any[];

  @Emit('on-select-row')
  onSelectRow(row: any, event: Event) {
    // console.log('on-select-row', row, event);
  }

  display(row: any, col: TableColumn) {
    const name = col.name;
    if (!name) return '';
    if (!name.includes('.')) return row[name];

    const names = name.split('.');
    let value = names.reduce((acum, cur) => acum[cur], row);
    if (col.formatFunc) {
      value = col.formatFunc(value);
    }
    return value;
  }

  /**
   * TDのCSS Classを決める
   */
  cellCss(col: TableColumn) {
    let cssClass = col.tdClass;
    if (!cssClass) {
      cssClass = {};
    }

    const newClass: any = cssClass;
    // console.log('aling', col.align);
    if (col.align === 'left') {
      newClass['text-left'] = true;
    } else if (col.align === 'right') {
      newClass['text-right'] = true;
    } else {
      newClass['align-middle'] = true;
    }
    return newClass;
  }

  sort(col: TableColumn, event: Event) {
    //　他のカラムは一旦クリア
    this.columns.filter((item) => item !== col).forEach((col) => ([col.up, col.down] = [false, false]));

    /**
     * 階層的なプロパティに対応して値を取得
     */
    const colItem = (name: string, value: any) => name.split('.').reduce((object, propName) => object[propName], value);

    /**
     * ソートの関数
     */
    const sortFunc = (isUp: boolean, a: any, b: any, name?: string, sortItem?: string) => {
      const lessThan = (a: any, b: any) => a > b;
      const operator = isUp ? 1 : -1;

      // ソートキーを確定
      const sortKey = col.name ? col.name : col.sortitem;
      if (!sortKey) return 0;

      // null を処理
      const valueA = colItem(sortKey, a);
      const valueB = colItem(sortKey, b);
      // console.log('valueAB', valueA, valueB);
      if (!valueA && !valueB) return 0;

      if (valueA !== 0 && !valueA) return 1 * operator;
      if (valueB !== 0 && !valueB) return -1 * operator;

      // 論理演算
      return lessThan(valueA, valueB) ? 1 * operator : -1 * operator;
    };

    // はじめはup
    if (!col.up || col.down) {
      col.up = true;
      col.down = !col.up;
    } else {
      col.up = false;
      col.down = !col.up;
    }

    this.rows.sort((a, b) => {
      // if (col.sortitem) console.log(colItem(col.sortitem, a), colItem(col.sortitem, b));
      return sortFunc(col.up!, a, b, col.name, col.sortitem);
    });

    // console.log(col);
  }
}
